import React from 'react';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import {
  buildAssemblyEstimatePrintLines,
  buildAssemblyEstimatePrintMeta,
  formatAssemblyPrintAmount,
  type AssemblyEstimatePrintDetail,
} from '@/utils/assemblyEstimatePrint';

export interface PrintableAssemblyEstimateProps {
  detail: AssemblyEstimatePrintDetail;
  onClose?: () => void;
}

/** Assembly-based estimation print — top-level assemblies with cost stack totals. */
export const PrintableAssemblyEstimate: React.FC<PrintableAssemblyEstimateProps> = ({ detail, onClose }) => {
  const estimate = detail.estimate || {};
  const currency = String(estimate.currency || 'AED');
  const stack = detail.stack || {};
  const estimated = Number(stack.estimatedCost ?? estimate.estimated_cost ?? 0);
  const selling = Number(stack.sellingPrice ?? estimate.selling_price ?? 0);
  const direct = Number(stack.directCost ?? estimate.direct_cost ?? 0);
  const margin = selling > 0 ? ((selling - estimated) / selling) * 100 : 0;

  return (
    <EngineCommercialA4Print
      docType="assembly_estimate"
      title="ASSEMBLY ESTIMATE"
      documentNumber={String(estimate.estimate_number || 'ABE')}
      partyName={String(estimate.customer_name || estimate.estimate_name || '')}
      onClose={onClose}
      landscape
      metaLines={buildAssemblyEstimatePrintMeta(detail)}
      introText={
        estimate.notes
          ? `Notes: ${String(estimate.notes).trim()}`
          : 'Assembly cost build-up with leaf materials and operations rolled to quotation assemblies.'
      }
      columns={[
        { key: 'sno', label: '#' },
        { key: 'description', label: 'Assembly' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'uom', label: 'UOM' },
        { key: 'strategy', label: 'Strategy' },
        { key: 'direct', label: 'Direct', align: 'right' },
        { key: 'estimated', label: 'Estimated', align: 'right' },
        { key: 'selling', label: 'Selling', align: 'right' },
      ]}
      lines={buildAssemblyEstimatePrintLines(detail)}
      finals={[{ id: 'totals', heightPx: 88 }]}
      finalBlocks={{
        totals: (
          <div className="print-letter-totals">
            <table>
              <tbody>
                <tr>
                  <td>Direct cost :</td>
                  <td>{formatAssemblyPrintAmount(direct, currency)}</td>
                </tr>
                <tr>
                  <td>Estimated cost :</td>
                  <td>{formatAssemblyPrintAmount(estimated, currency)}</td>
                </tr>
                <tr>
                  <td>Margin :</td>
                  <td>{margin.toFixed(1)}%</td>
                </tr>
                <tr className="grand-total">
                  <td>Selling price :</td>
                  <td>{formatAssemblyPrintAmount(selling, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
      }}
    />
  );
};

export default PrintableAssemblyEstimate;
